/** Readings library (STORY-064): curated sources per objective. AI suggestions arrive as
 * unverified drafts (violet) and never reach a student until the teacher verifies them. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import { TeacherChrome } from "../teacher/Chrome";
import { addReading, deleteReading, getCourse, getHostInfo, isOfflineError, listReadings, noServerHere, patchReading, readingSuggestion, suggestReadings, type Reading, type ReadingIn } from "../teacher/api";
import { Button, DraftChip, ExternalLink, useConfirm } from "../ui";

const KINDS = ["article", "book", "chapter", "video", "course", "other"] as const;

export function Readings() {
  const { courseId } = useParams({ strict: false }) as { courseId: string };
  const { t } = useTranslation();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const course = useQuery({ queryKey: ["course", courseId], queryFn: () => getCourse(courseId) });
  const readings = useQuery({ queryKey: ["readings", courseId], queryFn: () => listReadings(courseId) });
  const host = useQuery({ queryKey: ["host-info"], queryFn: getHostInfo, retry: false });
  const noAi = noServerHere(host.data);
  const [form, setForm] = useState<ReadingIn>({ lo_id: null, title: "", author: "", kind: "article", url: "", note: "" });
  const [job, setJob] = useState<{ lo: string; id: string } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["readings", courseId] });
  const add = useMutation({ mutationFn: () => addReading(courseId, form), onSuccess: () => { setForm({ ...form, title: "", author: "", url: "", note: "" }); setMsg(null); refresh(); }, onError: (e) => setMsg(String(e)) });
  const verify = useMutation({ mutationFn: (id: string) => patchReading(id, { verified: true }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: deleteReading, onSuccess: refresh });
  const suggest = useMutation({
    mutationFn: (loId: string) => suggestReadings(courseId, loId),
    onSuccess: (s, loId) => { setMsg(null); setJob({ lo: loId, id: s.job_id }); },
    onError: (e) => setMsg(isOfflineError(e) ? t("plan.needsAi") : e instanceof ApiError && e.code === "ai_unavailable" ? t("readings.aiUnavailable") : String(e)),
  });
  useEffect(() => {
    if (!job) return;
    const id = window.setInterval(async () => {
      try {
        const s = await readingSuggestion(courseId, job.id);
        if (s.status === "done" || s.status === "failed") {
          window.clearInterval(id);
          setJob(null);
          if (s.status === "failed") setMsg(s.error ?? t("readings.failed"));
          refresh();
        }
      } catch (e) { window.clearInterval(id); setJob(null); setMsg(String(e)); }
    }, 2500);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, courseId]);
  const los = course.data?.los ?? [];
  const groups: { lo_id: string | null; code: string; text: string; items: Reading[] }[] = [
    ...los.map((lo) => ({ lo_id: lo.lo_id, code: lo.code, text: lo.text, items: (readings.data ?? []).filter((r) => r.lo_id === lo.lo_id) })),
    { lo_id: null, code: "", text: t("readings.general"), items: (readings.data ?? []).filter((r) => r.lo_id === null) },
  ];
  const card = "rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm";
  const input = "field";

  return (
    <TeacherChrome title={t("readings.title")} course={course.data ? { id: courseId, title: course.data.title } : null} stage="items" wide>
      <p className="measure -mt-2 text-sm text-[color:var(--ink-2)]">{t("readings.lede")}</p>

      <form className={`${card} grid gap-3 sm:grid-cols-2`} onSubmit={(e) => { e.preventDefault(); add.mutate(); }} aria-label={t("readings.add")}>
        <label className="flex flex-col gap-1 text-sm sm:col-span-2">{t("readings.titleField")}<input className={input} required maxLength={300} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
        <label className="flex flex-col gap-1 text-sm">{t("readings.objective")}
          <select className={input} value={form.lo_id ?? ""} onChange={(e) => setForm({ ...form, lo_id: e.target.value || null })}>
            <option value="">{t("readings.general")}</option>
            {los.map((lo) => <option key={lo.lo_id} value={lo.lo_id}>{lo.code} {lo.text}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">{t("readings.kind")}
          <select className={input} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as Reading["kind"] })}>
            {KINDS.map((k) => <option key={k} value={k}>{t(`readings.kinds.${k}`)}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">{t("readings.author")}<input className={input} maxLength={200} value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} /></label>
        <label className="flex flex-col gap-1 text-sm">{t("readings.url")}<input className={input} type="url" maxLength={500} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></label>
        <label className="flex flex-col gap-1 text-sm sm:col-span-2">{t("readings.note")}<input className={input} maxLength={400} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></label>
        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" loading={add.isPending} disabled={!form.title.trim()}>{t("readings.add")}</Button>
          {msg && <p role="alert" className="text-sm" style={{ color: "var(--danger)" }}>{msg}</p>}
        </div>
      </form>

      {noAi && <p id="no-ai" className="text-sm text-[color:var(--ink-2)]">{t("plan.needsAi")}</p>}

      {groups.map((g) => (
        <section key={g.lo_id ?? "general"} className={card} aria-labelledby={`rd-${g.lo_id ?? "general"}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 id={`rd-${g.lo_id ?? "general"}`} className="font-semibold"><span className="font-mono">{g.code}</span> {g.text}</h2>
            {g.lo_id && (
              <span className="flex items-center gap-2">
                <DraftChip />
                <Button variant="tertiary" aria-describedby={noAi ? "no-ai" : undefined} loading={job?.lo === g.lo_id} disabled={!!job || noAi} onClick={() => suggest.mutate(g.lo_id!)}>{t("readings.suggest")}</Button>
              </span>
            )}
          </div>
          {job?.lo === g.lo_id && <p className="text-sm text-[color:var(--ink-2)]" aria-live="polite">{t("readings.suggesting")}</p>}
          {g.items.length === 0 ? (
            <p className="mt-2 text-sm text-[color:var(--ink-2)]">{t("readings.none")}</p>
          ) : (
            <ul className="mt-2 flex flex-col gap-2">
              {g.items.map((r) => (
                <li key={r.reading_id} className="flex flex-wrap items-start justify-between gap-2 rounded-[var(--radius-control)] border p-3 text-sm" style={{ borderColor: r.verified ? "var(--border, #ddd)" : "var(--draft)", borderStyle: r.verified ? "solid" : "dashed" }}>
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full border px-2 text-xs">{t(`readings.kinds.${r.kind}`)}</span>
                      {r.url ? <ExternalLink href={r.url} className="font-medium underline">{r.title}</ExternalLink> : <span className="font-medium">{r.title}</span>}
                      {r.author && <span className="text-[color:var(--ink-2)]">{r.author}</span>}
                      {!r.verified && <DraftChip />}
                      {!r.verified && <span className="text-xs" style={{ color: "var(--draft)" }}>{t("readings.unverified")}</span>}
                    </p>
                    {r.note && <p className="mt-1 text-xs text-[color:var(--ink-2)]">{r.note}</p>}
                  </div>
                  <div className="flex gap-1">
                    {!r.verified && <Button variant="secondary" loading={verify.isPending} onClick={() => verify.mutate(r.reading_id)}>{t("readings.verify")}</Button>}
                    <Button
                      variant="tertiary"
                      onClick={async () => {
                        if (await confirm({ title: t("readings.removeTitle"), body: t("readings.removeConfirm"), confirmLabel: t("readings.remove"), destructive: true })) remove.mutate(r.reading_id);
                      }}
                    >
                      {t("readings.remove")}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </TeacherChrome>
  );
}
